import React from 'react';
import { Image, Text } from '@vmos2/design-system';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useComponentTheme } from '@vmos2/design-system/src/useComponentTheme';

type Props = {
  variant: 'google' | 'apple',
  buttonImageUrl: string,
  getWalletPass: () => void,
  isLoading: boolean,
  hasError: boolean,
  isApplicableDevice: boolean,
  mode?: string,
  width?: string,
};

export const WalletPassButton = ({
  variant,
  buttonImageUrl,
  getWalletPass,
  isLoading,
  hasError,
  isApplicableDevice,
  mode,
  width,
}: Props) => {
  const { t } = useTranslation('profile.rewards');
  const componentTheme = useComponentTheme(theme => theme.components.WalletPass, mode);

  if (!isApplicableDevice || !buttonImageUrl) {
    return null;
  }

  const modePrefix = `${`${mode}:` || ''}AddWalletPassToWallet`;

  return (
    <Container data-test={`add-to-${variant}-wallet`} componentTheme={componentTheme}>
      <ButtonWrapper onClick={getWalletPass} data-test={`add-to-${variant}-wallet-btn`} width={width}>
        <ImageWrapper
          data-test={`add-to-${variant}-wallet-image`}
          src={buttonImageUrl}
          alt={t(`${variant}WalletPass.imageAltText`)}
        />
      </ButtonWrapper>
      {isLoading && (
        <Text
          ml={1}
          mt={2}
          level={5}
          mode={`${modePrefix}:Loading`}
          display="inline-block"
          dataTest={`add-to-${variant}-wallet-loading`}
        >
          {t(`${variant}WalletPass.downloadPending`)}
        </Text>
      )}
      {hasError && (
        <Text
          ml={1}
          mt={2}
          level={5}
          mode={`${modePrefix}:Error`}
          display="inline-block"
          bold
          type="danger"
          dataTest={`add-to-${variant}-wallet-error`}
          whiteSpace='pre-line'
        >
          {t(`${variant}WalletPass.unableToAddPass`)}
        </Text>
      )}
    </Container>
  );
};

const Container = styled.div<{ componentTheme?: { align?: string } }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  ${({ componentTheme: { align } = {} }) => align ? `align-items: ${align};` : ''}
`;

const ImageWrapper = styled(Image)`
  width: 100%;
  height: auto;
`;

const ButtonWrapper =
  styled.button <
  { width?: string } >
  `
  background: none;
  border: none;
  width: ${({ width }) => width || '100%'};
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  padding: 0;
`;
